import { keepPreviousData, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { Address } from './address'
import { Document } from './document'
import { CoiMode } from './insurancePolicy'

/** Just enough of a policy to label the certificate it appears on. */
export interface COIPolicySummary {
  id: string
  label: string
  vendorName: string
  coiMode: CoiMode
}

export interface COIDocument {
  id: string
  dateCreated: string
  dateUpdated: string
  org: string
  document: Document
  requestEmail: string
  version: number
  /**
   * The policies this certificate states. Optional because a backend predating
   * selections returns no key at all (deploy skew), so every read must fall
   * back to [].
   */
  policies?: COIPolicySummary[]
  selectionKey?: string
  /**
   * Printed in the DESCRIPTION OF OPERATIONS box of every holder on this
   * certificate that has no description of its own. Cai admins only — see
   * CertificateSettingsDialog.
   */
  descriptionOfOperations?: string
  /**
   * Whether holders of this certificate may schedule vehicles on it. Optional
   * because a backend predating the field returns no key at all (deploy skew),
   * so every read must treat undefined as allowed.
   */
  enableRemarks?: boolean
  /**
   * Whether a policy change rebuilds this certificate. Off pins it to the
   * document on file. Optional because a backend predating the field returns
   * nothing, so every read must default to on.
   */
  autoRegenerate?: boolean
}

/** Whether a certificate lets its holders schedule vehicles. Undefined = yes. */
export function remarksEnabled(document?: COIDocument | null): boolean {
  return document?.enableRemarks !== false
}

/**
 * The certificates a holder with this selection would be issued.
 *
 * Narrows to one once a policy selection is known; before that it is every
 * certificate the org holds, so callers must decide what an ambiguous answer
 * means rather than assume the first row.
 */
export function documentsForSelection(
  documents: COIDocument[],
  { selectionKey, policyIds }: { selectionKey?: string; policyIds?: string[] }
): COIDocument[] {
  if (selectionKey) {
    return documents.filter((document) => document.selectionKey === selectionKey)
  }
  if (policyIds?.length) {
    const wanted = new Set(policyIds)
    return documents.filter((document) => {
      const ids = (document.policies ?? []).map((policy) => policy.id)
      return ids.length === wanted.size && ids.every((id) => wanted.has(id))
    })
  }
  return documents
}

/**
 * A certificate the org could hold, whether or not it has been generated yet.
 * Manual selections never have a document — each holder's copy is uploaded by a
 * Cai admin on request.
 */
export interface COISelection {
  selectionKey: string
  policies: COIPolicySummary[]
  isManual: boolean
  document: COIDocument | null
}

export interface COICertificateHolder {
  id: string
  dateCreated: string
  dateUpdated: string
  org: string
  name: string
  email: string
  address: Address | null
  // The list only reports whether a COI exists — its URL is a signed GCS URL,
  // which costs a signing round trip per holder to produce, so it is signed per
  // download behind certificateHolderDownloadHref().
  hasDocument: boolean
  // Whether the email on file can actually be sent to — decided by the backend,
  // which gates sending on the same check.
  hasValidEmail: boolean
  descriptionOfOperations: string
  truckVins: string[]
  autoRenew: boolean
  includeTrucks: boolean
  includeTrailers: boolean
  isAutomatic: boolean
  isActive: boolean
  isDeleted: boolean
  dateLastSent: string | null
  hasLatestVersionSent: boolean
  /** The policies whose certificate this holder receives. Optional: deploy skew. */
  policies?: COIPolicySummary[]
  selectionKey?: string
}

export interface COISentLog {
  id: string
  dateCreated: string
  dateUpdated: string
  coiDocument: string
  coiDocumentVersion: number
  certificateHolder: string
  createdBy: string | null
  createdByName: string | null
  certificateHolderEmail: string
  hasDocument: boolean
  isLatestVersion: boolean
  emailMessage: string | null
  dateSent: string
}

export interface CreateCertificateHolderData {
  name: string
  email: string
  address?: {
    line1: string
    line2?: string
    city: string
    state: string
    zip: string
  }
  descriptionOfOperations?: string
  autoRenew: boolean
  includeTrucks?: boolean
  includeTrailers?: boolean
  send?: boolean
  skipGeneration?: boolean
  /** Which certificate this holder receives. Omit for the org's default. */
  policyIds?: string[]
}

export interface ImportCertificateHoldersResponse {
  status: string
  documentId: string
  taskId: string
}

export enum CertificateHolderImportStatus {
  RUNNING = 'running',
  DONE = 'done',
  ERROR = 'error',
}

export interface CertificateHolderImportResult {
  status: CertificateHolderImportStatus
  error?: string
  created?: number
  updated?: number
  unchanged?: number
  failed?: number
  truncated?: number
}

export function orgParams(orgId?: string) {
  return orgId ? { org_id: orgId } : {}
}

/**
 * Hands a holder list off to the backend, which parses it and creates every
 * holder in the background — a real list runs to thousands of rows, so the
 * response only confirms the import started. Poll the holder list to watch them
 * arrive.
 */
export function importCertificateHolders(
  file: File,
  orgId?: string
): Promise<ImportCertificateHoldersResponse> {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post('/api/v1/insure/certificate-holders/import/', formData, { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as ImportCertificateHoldersResponse)
}

/** How the background import of a previous upload went. */
export function getCertificateHolderImportStatus(
  documentId: string,
  orgId?: string
): Promise<CertificateHolderImportResult> {
  return axios
    .get('/api/v1/insure/certificate-holders/import-status/', {
      params: { document_id: documentId, ...orgParams(orgId) },
    })
    .then((response) => toCamelCase(response.data) as CertificateHolderImportResult)
}

export function createCertificateHolder(
  data: CreateCertificateHolderData,
  orgId?: string
): Promise<COICertificateHolder> {
  return axios
    .post('/api/v1/insure/certificate-holders/', toSnakeCase(data), { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COICertificateHolder)
}

export function updateCertificateHolder(
  id: string,
  data: Partial<CreateCertificateHolderData>,
  orgId?: string
): Promise<COICertificateHolder> {
  return axios
    .patch(`/api/v1/insure/certificate-holders/${id}/`, toSnakeCase(data), { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COICertificateHolder)
}

export function deleteCertificateHolder(id: string, orgId?: string): Promise<void> {
  return axios.delete(`/api/v1/insure/certificate-holders/${id}/`, { params: orgParams(orgId) })
}

export function uploadCertificateHolderDocument(
  id: string,
  file: File,
  orgId?: string
): Promise<COICertificateHolder> {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`/api/v1/insure/certificate-holders/${id}/upload-document/`, formData, { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COICertificateHolder)
}

/** How many holders a "send to all" would actually reach. */
export function getSendableHolderCount(orgId?: string): Promise<number> {
  return axios
    .get('/api/v1/insure/certificate-holders/send-all/', { params: orgParams(orgId) })
    .then((response) => (toCamelCase(response.data) as { sendableCount: number }).sendableCount)
}

/** Starts one background send per holder; resolves with how many were dispatched. */
export function sendCOIToAllHolders(orgId?: string, ccTeam = false): Promise<number> {
  return axios
    .post('/api/v1/insure/certificate-holders/send-all/', toSnakeCase({ ccTeam }), {
      params: orgParams(orgId),
    })
    .then((response) => (toCamelCase(response.data) as { dispatched: number }).dispatched)
}

export function useSendableHolderCount(orgId?: string, enabled = true) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_SENDABLE_HOLDERS, orgId],
    queryFn: () => getSendableHolderCount(orgId),
    enabled,
  })
  return { sendableCount: data ?? 0, isLoading }
}

export function sendCOI(id: string, orgId?: string): Promise<COICertificateHolder> {
  return axios
    .post(`/api/v1/insure/certificate-holders/${id}/send-coi/`, null, { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COICertificateHolder)
}

export function regenerateCOI(coiDocumentId: string, orgId?: string): Promise<void> {
  return axios.post(`/api/v1/insure/coi-documents/${coiDocumentId}/regenerate/`, null, {
    params: orgParams(orgId),
  })
}

/** One holder named in a bulk-move preview. */
export interface BulkMoveHolder {
  id: string
  idShort: string
  name: string
  email: string
  conflictsWith?: string
  address?: string
}

/** What moving one certificate's holders onto another would do. */
export interface BulkMovePreview {
  targetVersion: number
  targetIdShort: string
  holderCount: number
  movableCount: number
  /** Blocking: the email is already spoken for on the target certificate. */
  conflicts: BulkMoveHolder[]
  /** Not blocking: one inbox would hold the target certificate twice. */
  overlaps: BulkMoveHolder[]
  /** Moved, but their hand-uploaded PDF is left alone. */
  manual: BulkMoveHolder[]
  /** The target takes no vehicle schedule, so their flags are forced off. */
  losingSchedule: BulkMoveHolder[]
}

export function getBulkMovePreview(
  sourceSelectionKey: string,
  policyIds: string[],
  orgId?: string
): Promise<BulkMovePreview> {
  return axios
    .get('/api/v1/insure/certificate-holders/bulk-selection/', {
      params: {
        ...orgParams(orgId),
        source_selection_key: sourceSelectionKey,
        policy_ids: policyIds,
      },
      // Repeated `policy_ids=` rather than a comma-joined value, which is what
      // DRF's getlist reads on the other side.
      paramsSerializer: { indexes: null },
    })
    .then((response) => toCamelCase(response.data) as BulkMovePreview)
}

/** Moves the holders and starts one regeneration each; resolves with how many moved. */
export function bulkMoveHolders(
  sourceSelectionKey: string,
  policyIds: string[],
  orgId?: string
): Promise<number> {
  return axios
    .post(
      '/api/v1/insure/certificate-holders/bulk-selection/',
      toSnakeCase({ sourceSelectionKey, policyIds }),
      {
        params: orgParams(orgId),
      }
    )
    .then((response) => (toCamelCase(response.data) as { moved: number }).moved)
}

export function useBulkMovePreview(
  sourceSelectionKey: string,
  policyIds: string[],
  orgId?: string,
  enabled = true
) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_BULK_MOVE_PREVIEW, orgId, sourceSelectionKey, policyIds],
    queryFn: () => getBulkMovePreview(sourceSelectionKey, policyIds, orgId),
    enabled: enabled && !!sourceSelectionKey && policyIds.length > 0,
  })
  return { preview: data, isLoading }
}

export interface COIDocumentSettings {
  descriptionOfOperations: string
  enableRemarks: boolean
}

/**
 * Sets one certificate's own settings (Cai admins only).
 *
 * A changed description mints a new version and rebuilds every automatic holder
 * on this certificate — the text only reaches a holder through their
 * regenerated PDF. Flipping enableRemarks alone changes no document.
 */
export function updateCOISettings(
  coiDocumentId: string,
  settings: COIDocumentSettings,
  orgId?: string
): Promise<COIDocument> {
  return axios
    .post(`/api/v1/insure/coi-documents/${coiDocumentId}/settings/`, toSnakeCase(settings), {
      params: orgParams(orgId),
    })
    .then((response) => toCamelCase(response.data) as COIDocument)
}

/** Turns automatic rebuilding of one certificate on or off. */
export function setCOIAutoRegenerate(
  coiDocumentId: string,
  autoRegenerate: boolean,
  orgId?: string
): Promise<COIDocument> {
  return axios
    .post(
      `/api/v1/insure/coi-documents/${coiDocumentId}/auto-regenerate/`,
      { auto_regenerate: autoRegenerate },
      { params: orgParams(orgId) }
    )
    .then((response) => toCamelCase(response.data) as COIDocument)
}

export function uploadCOITemplate(file: File, orgId?: string, selectionKey?: string): Promise<COIDocument> {
  const formData = new FormData()
  formData.append('file', file)
  // Omitted for the org's headline certificate, so the single-button upload
  // keeps working unchanged.
  if (selectionKey) formData.append('selection_key', selectionKey)

  return axios
    .post('/api/v1/insure/coi-documents/upload-template/', formData, { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COIDocument)
}

/** The newest version of every certificate the org holds, one per selection. */
function getCurrentCOIDocuments(orgId?: string): Promise<COIDocument[]> {
  return axios
    .get('/api/v1/insure/coi-documents/current/', { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COIDocument[])
}

export function useCurrentCOIDocuments(orgId?: string, { enabled = true }: { enabled?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_DOCUMENTS_CURRENT, orgId],
    queryFn: () => getCurrentCOIDocuments(orgId),
    enabled,
  })
  return { documents: data ?? [], isLoading }
}

/**
 * The policies a certificate can be issued for right now.
 *
 * A policy only appears once its certificate has been generated, which needs its
 * policy document uploaded and parsed — so an automatic policy missing from this
 * set has nothing to send yet.
 */
export function certifiedPolicyIds(documents: COIDocument[]): Set<string> {
  return new Set(documents.flatMap((document) => document.policies ?? []).map((policy) => policy.id))
}

export function coiDocumentDownloadHref(id: string, orgId?: string): string {
  return downloadHref(`/api/v1/insure/coi-documents/${id}/download/`, orgId)
}

function getLatestCOIDocument(orgId?: string): Promise<COIDocument | null> {
  return axios
    .get('/api/v1/insure/coi-documents/latest/', { params: orgParams(orgId) })
    .then((response) => (response.status === 204 ? null : (toCamelCase(response.data) as COIDocument)))
}

// Plain hrefs, not fetches: the endpoints redirect to a signed GCS URL, so a
// link works without script and the URL is signed per download rather than once
// per row of the list (each one costs an IAM signBlob round trip).
function downloadHref(path: string, orgId?: string): string {
  return orgId ? `${path}?org_id=${encodeURIComponent(orgId)}` : path
}

export function certificateHolderDownloadHref(id: string, orgId?: string): string {
  return downloadHref(`/api/v1/insure/certificate-holders/${id}/download/`, orgId)
}

export function coiSentLogDownloadHref(id: string, orgId?: string): string {
  return downloadHref(`/api/v1/insure/coi-sent-logs/${id}/download/`, orgId)
}

function getCertificateHolders(
  page: number,
  search: string,
  orgId?: string
): Promise<PaginatedResults<COICertificateHolder>> {
  return axios
    .get('/api/v1/insure/certificate-holders/', {
      params: {
        ...(page > 1 ? { page } : {}),
        ...(search ? { search } : {}),
        ...orgParams(orgId),
      },
    })
    .then((response) => toCamelCase(response.data) as PaginatedResults<COICertificateHolder>)
}

function getCertificateHolder(id: string, orgId?: string): Promise<COICertificateHolder> {
  return axios
    .get(`/api/v1/insure/certificate-holders/${id}/`, { params: orgParams(orgId) })
    .then((response) => toCamelCase(response.data) as COICertificateHolder)
}

function getCOISentLogs(certificateHolderId?: string, orgId?: string): Promise<PaginatedResults<COISentLog>> {
  const params = {
    ...(certificateHolderId ? { certificate_holder: certificateHolderId } : {}),
    ...orgParams(orgId),
  }
  return axios
    .get('/api/v1/insure/coi-sent-logs/', { params })
    .then((response) => toCamelCase(response.data) as PaginatedResults<COISentLog>)
}

export function useLatestCOIDocument(orgId?: string) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_DOCUMENT_LATEST, orgId],
    queryFn: () => getLatestCOIDocument(orgId),
  })
  return { coiDocument: data ?? null, isLoading }
}

export function useCertificateHolders(orgId?: string, page = 1, search = '') {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS, orgId, page, search],
    queryFn: () => getCertificateHolders(page, search, orgId),
    // An out-of-range page 404s (the last holder on a page was deleted); the
    // caller falls back to page 1 rather than retrying a page that cannot exist.
    retry: false,
    // Page and search are part of the key, so every keystroke and page change
    // would otherwise be an uncached key with no data — leaving isLoading true
    // and blanking the table. Keeping the previous results on screen means the
    // list updates in place instead of flickering through a loading state.
    placeholderData: keepPreviousData,
  })
  return {
    holders: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    // isLoading is the first load only (nothing to show yet); isFetching also
    // covers refreshes that have stale results on screen.
    isLoading,
    isFetching,
  }
}

/**
 * A single holder, for opening one that is not on the current page — a deep link
 * from a "COI requested" notification can name any holder in a list of thousands.
 */
export function useCertificateHolder(id: string | null, orgId?: string) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_CERTIFICATE_HOLDER, id, orgId],
    queryFn: () => getCertificateHolder(id!, orgId),
    enabled: !!id,
    retry: false,
  })
  return { holder: data ?? null, isLoading }
}

/**
 * Sent logs, for one holder or (with no id) the whole org.
 *
 * `enabled: false` is for callers mounted before there is anything to fetch —
 * without it, a holder-scoped caller with no holder yet would ask for the org's
 * entire log instead of nothing.
 */
export function useCOISentLogs(
  certificateHolderId?: string,
  orgId?: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_SENT_LOGS, certificateHolderId, orgId],
    queryFn: () => getCOISentLogs(certificateHolderId, orgId),
    enabled,
  })
  return { logs: data?.results ?? [], count: data?.count ?? 0, isLoading }
}
